
import Vue, { PropType } from 'vue'
import { apiService } from '@/service/api.service'

interface LicenseConfiguration {
  licenceTypeId: number | null;
  quantity: number;
  keyCount: number;
  deviceTypeRestriction: string | null;
}
interface LicenseType {
  id: number;
  label: string;
}

export default Vue.extend({
  name: 'CreateActivationKeyDialog',
  props: {
    value: { type: Boolean, required: true },
    licenseTypes: { type: Array as PropType<LicenseType[]>, default: () => [] },
    creating: { type: Boolean, default: false }
  },
  data () {
    return {
      formValid: false,
      dateMenu: false,
      licenseConfigurations: [] as LicenseConfiguration[],
      formData: {
        duration_days: 365,
        expires_at: null as string | null,
        notes: '',
        user_id: null as number | null
      },
      deviceTypes: [
        { text: 'Any Device', value: null },
        { text: 'Thor Only', value: 'thor' },
        { text: 'Spark Only', value: 'spark' }
      ],
      users: [] as Array<{ id: number; email: string; name: string }>,
      loadingUsers: false
    }
  },
  computed: {
    dialog: {
      get (): boolean { return this.value },
      set (val: boolean) { this.$emit('input', val) }
    },
    isFormComplete (): boolean {
      return (
        this.licenseConfigurations.length > 0 &&
        this.licenseConfigurations.every((c) => c.licenceTypeId !== null && c.quantity > 0 && c.keyCount > 0) &&
        this.formValid
      )
    }
  },
  watch: {
    value (val: boolean) {
      if (val && this.licenseConfigurations.length === 0) this.addLicenseConfig()
      if (val && this.users.length === 0) this.loadUsers()
    }
  },
  mounted () { this.loadUsers() },
  methods: {
    async loadUsers () {
      this.loadingUsers = true
      try {
        const response = await apiService.get('/admin-get-users')
        if (response.data.status && Array.isArray(response.data.data)) {
          this.users = response.data.data.map((u: any) => ({ id: u.id, email: u.email, name: u.name || u.email }))
        }
      } catch (e) { console.error('Error loading users:', e) } finally { this.loadingUsers = false }
    },
    onDialogChange (val: boolean) { if (!val) this.closeDialog() },
    closeDialog () { this.resetForm(); this.$emit('input', false); this.$emit('close') },
    resetForm () {
      this.licenseConfigurations = []
      this.formData = { duration_days: 365, expires_at: null, notes: '', user_id: null }
      const form = this.$refs.createForm as Vue & { resetValidation: () => void }
      if (form) form.resetValidation()
    },
    addLicenseConfig () {
      this.licenseConfigurations.push({ licenceTypeId: null, quantity: 1, keyCount: 1, deviceTypeRestriction: null })
    },
    removeLicenseConfig (index: number) { this.licenseConfigurations.splice(index, 1) },
    createKey () {
      const form = this.$refs.createForm as Vue & { validate: () => boolean }
      if (!form.validate()) { this.$emit('error', 'Please fill in all required fields'); return }
      if (this.licenseConfigurations.length === 0) { this.$emit('error', 'Please add at least one license configuration'); return }
      if (this.licenseConfigurations.some((c) => c.licenceTypeId === null || c.quantity <= 0 || c.keyCount <= 0)) {
        this.$emit('error', 'Please fill in all required fields for each license configuration'); return
      }
      /* eslint-disable camelcase */
      const payload = {
        licence_configurations: this.licenseConfigurations.map((c) => ({
          licence_type_id: c.licenceTypeId as number,
          quantity: c.quantity,
          key_count: c.keyCount,
          device_type_restriction: c.deviceTypeRestriction
        })),
        duration_days: this.formData.duration_days,
        expires_at: this.formData.expires_at ? new Date(this.formData.expires_at).toISOString() : null,
        notes: this.formData.notes,
        user_id: this.formData.user_id
      }
      /* eslint-enable camelcase */
      this.$emit('create', payload)
    }
  }
})
